import { useState, useEffect, useMemo } from "react";
import { ConfirmationRequest as ConfirmationRequestType, useCopilotStore } from "@/app/lib/stores/useCopilotStore";
import { ShieldCheckIcon, ChevronRightIcon, ClockIcon, XCircleIcon, CheckCircleIcon, ExclamationCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { cn } from "@/app/lib/utils";
import { useProjects } from "@/app/lib/hooks/queries/useProjects";
import { useCustomers } from "@/app/lib/hooks/queries/useCustomers";
import { useUploadProjectThumbnail } from "@/app/lib/hooks/queries/useStorage";
import { AttachmentDropdown } from "@/app/components/ui/AttachmentDropdown";
import Image from "next/image";
import toast from "react-hot-toast";

interface ConfirmationRequestProps {
  request: ConfirmationRequestType;
  onApprove: (reason?: string, data?: any) => void;
  onDeny: (reason?: string) => void;
}

interface ResolvedDetails {
  [key: string]: {
    value: any;
    displayName?: string;
  };
}

export function ConfirmationRequest({
  request,
  onApprove,
  onDeny,
}: ConfirmationRequestProps) {
  const [status, setStatus] = useState<"pending" | "approved" | "denied">("pending");
  const [resolvedDetails, setResolvedDetails] = useState<ResolvedDetails>({});
  const [selectedImage, setSelectedImage] = useState<{ file: File; previewUrl: string } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const toolExecutions = useCopilotStore((state) => state.toolExecutions);
  const storeUploadedImage = useCopilotStore((state) => state.storeUploadedImage);
  const uploadProjectThumbnail = useUploadProjectThumbnail();
  
  // Generate project ID for create_project requests - stable across re-renders
  const projectId = useMemo(() => {
    if (request.tool === "create_project") {
      return crypto.randomUUID();
    }
    return null;
  }, [request.tool, request.id]); // Include request.id to ensure uniqueness per request
  
  // Get tool execution status if available
  const toolExecution = request.tool_call_id ? toolExecutions.get(request.tool_call_id) : null;
  
  // Fetch data for entity resolution
  const { data: projects } = useProjects();
  const { data: customers } = useCustomers();

  // Resolve entity names
  useEffect(() => {
    const resolved: ResolvedDetails = {};
    
    Object.entries(request.details).forEach(([key, value]) => {
      resolved[key] = { value };
      
      // Resolve project
      if (key === "project_id" && projects) {
        const project = projects.find(p => p.id === value);
        if (project) {
          resolved[key].displayName = project.name;
        }
      }
      
      // Resolve customer
      if (key === "customer_id" && customers) {
        const customer = customers.find(c => c.id === value);
        if (customer) {
          resolved[key].displayName = customer.name;
        }
      }
    });
    
    setResolvedDetails(resolved);
  }, [request.details, projects, customers]);

  const handleApprove = async () => {
    if (request.tool === "create_project" && selectedImage && projectId) {
      // Upload image first
      setIsUploading(true);
      try {
        const result = await uploadProjectThumbnail.mutateAsync({
          projectId,
          file: selectedImage.file,
        });

        const confirmationData = { 
          image_url: result.publicUrl,
          project_id: projectId 
        };
        // Store uploaded image URL in store for potential cleanup
        if (request.tool_call_id) {
          storeUploadedImage(request.tool_call_id, result.publicUrl);
        }
        setStatus("approved");
        onApprove(undefined, confirmationData);
      } catch (error) {
        console.error("Failed to upload image:", error);
        toast.error("Failed to upload image. Creating project without image.");
        const fallbackData = { project_id: projectId };
        setStatus("approved");
        onApprove(undefined, fallbackData);
      } finally {
        setIsUploading(false);
      }
    } else if (request.tool === "create_project" && projectId) {
      // No image, but still need to pass project_id
      const confirmationData = { project_id: projectId };
      setStatus("approved");
      onApprove(undefined, confirmationData);
    } else {
      setStatus("approved");
      onApprove();
    }
  };

  const handleDeny = () => {
    // Clean up image preview URL if needed
    if (selectedImage?.previewUrl) {
      URL.revokeObjectURL(selectedImage.previewUrl);
    }
    setStatus("denied");
    onDeny("User denied the action");
  };

  // Handle image selection
  const handleImageSelect = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      // Clean up previous preview URL
      if (selectedImage?.previewUrl) {
        URL.revokeObjectURL(selectedImage.previewUrl);
      }
      const previewUrl = URL.createObjectURL(file);
      setSelectedImage({ file, previewUrl });
    }
  };

  // Handle image removal
  const handleImageRemove = () => {
    if (selectedImage?.previewUrl) {
      URL.revokeObjectURL(selectedImage.previewUrl);
    }
    setSelectedImage(null);
  };

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (selectedImage?.previewUrl) {
        URL.revokeObjectURL(selectedImage.previewUrl);
      }
    };
  }, [selectedImage?.previewUrl]);
  
  // Note: We don't cleanup uploaded images on unmount because:
  // 1. The tool might still be executing and succeed
  // 2. The cleanup effect above handles failed executions
  // 3. Successful executions will use the image in the created project

  // Format the action description
  const getActionDescription = () => {
    if (request.tool === "update_project") {
      return "Update project";
    } else if (request.tool === "create_project") {
      return "Create new project";
    }
    return request.action;
  };

  // Format field names
  const formatFieldName = (field: string) => {
    return field
      .replace(/_/g, " ")
      .replace(/id$/i, "")
      .replace(/^\w/, c => c.toUpperCase());
  };

  // Check if this is an update action with old/new values
  const isUpdate = request.tool.includes("update");

  // Determine display status based on tool execution
  const displayStatus = toolExecution?.status === 'failed' 
    ? (toolExecution.failure_type === 'timeout' ? 'timeout' : 
       toolExecution.failure_type === 'denied' ? 'denied' : 'error')
    : toolExecution?.status === 'completed' ? 'completed' 
    : status;

  return (
    <div
      className={cn(
        "relative border rounded-lg p-4 transition-all duration-200",
        displayStatus === "pending" && "bg-blue-50 border-blue-200",
        displayStatus === "approved" && "bg-green-50 border-green-200",
        displayStatus === "completed" && "bg-green-50 border-green-200",
        displayStatus === "denied" && "bg-red-50 border-red-200",
        displayStatus === "timeout" && "bg-orange-50 border-orange-200",
        displayStatus === "error" && "bg-red-50 border-red-200"
      )}
    >
      {/* Status indicator */}
      {displayStatus !== "pending" && (
        <div className="absolute top-2 right-2">
          <span
            className={cn(
              "text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1",
              displayStatus === "approved" && "bg-green-100 text-green-700",
              displayStatus === "completed" && "bg-green-100 text-green-700",
              displayStatus === "denied" && "bg-red-100 text-red-700",
              displayStatus === "timeout" && "bg-orange-100 text-orange-700",
              displayStatus === "error" && "bg-red-100 text-red-700"
            )}
          >
            {displayStatus === "timeout" && <ClockIcon className="w-3 h-3" />}
            {displayStatus === "error" && <ExclamationCircleIcon className="w-3 h-3" />}
            {displayStatus === "completed" && <CheckCircleIcon className="w-3 h-3" />}
            {displayStatus === "denied" && <XCircleIcon className="w-3 h-3" />}
            {displayStatus === "approved" ? "Approved" : 
             displayStatus === "denied" ? "Denied" :
             displayStatus === "completed" ? "Completed" :
             displayStatus === "timeout" ? "Timed out" :
             displayStatus === "error" ? "Failed" : ""}
          </span>
        </div>
      )}

      <div className="flex items-start gap-3">
        <ShieldCheckIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              {getActionDescription()}
            </h4>
          </div>

          {/* Details section */}
          <div className="space-y-2 mb-3">
            {Object.entries(resolvedDetails).map(([key, data]) => {
              // Format the display value properly for different types
              let displayValue = data.displayName || data.value;
              
              // Handle boolean values explicitly
              if (typeof data.value === "boolean") {
                displayValue = data.value ? "Yes" : "No";
              } else if (data.value === null || data.value === undefined) {
                displayValue = "(empty)";
              } else if (typeof data.value === "object") {
                // Handle arrays and objects
                displayValue = Array.isArray(data.value) 
                  ? `[${data.value.length} items]`
                  : JSON.stringify(data.value);
              }
              
              const isUuid = typeof data.value === "string" && 
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.value);
              
              // For update actions, check if we have old value
              const oldValue = request.details[`old_${key}`];
              const isChange = isUpdate && oldValue !== undefined;
              
              // Format old value for display
              let formattedOldValue = oldValue;
              if (typeof oldValue === "boolean") {
                formattedOldValue = oldValue ? "Yes" : "No";
              } else if (oldValue === null || oldValue === undefined) {
                formattedOldValue = "(empty)";
              } else if (typeof oldValue === "object") {
                formattedOldValue = Array.isArray(oldValue) 
                  ? `[${oldValue.length} items]`
                  : JSON.stringify(oldValue);
              }
              
              return (
                <div key={key} className="flex items-start gap-2 text-sm">
                  <span className="text-gray-500 min-w-[100px]">
                    {formatFieldName(key)}:
                  </span>
                  <span className="text-gray-800">
                    {isChange ? (
                      <span className="flex items-center gap-1">
                        <span className="line-through text-gray-400">
                          {formattedOldValue}
                        </span>
                        <ChevronRightIcon className="w-3 h-3 text-gray-400" />
                        <span className="font-medium">
                          {displayValue}
                        </span>
                      </span>
                    ) : (
                      <span>
                        {displayValue}
                        {data.displayName && isUuid && (
                          <span className="text-gray-400 text-xs ml-1">
                            ({data.value.slice(0, 8)}...)
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
            
            {/* Image attachment row for create_project - seamlessly integrated */}
            {request.tool === "create_project" && status === "pending" && (!toolExecution || toolExecution.status === "started") && (
              <div className="flex items-start gap-2 text-sm">
                <span className="text-gray-500 min-w-[100px]">
                  Image:
                </span>
                <span className="text-gray-800">
                  {selectedImage ? (
                    <div className="flex items-center gap-2">
                      <div className="relative w-8 h-8 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                        <Image
                          src={selectedImage.previewUrl}
                          alt="Preview"
                          fill
                          className="object-cover"
                        />
                      </div>
                      <span className="text-sm">{selectedImage.file.name}</span>
                      <button
                        onClick={handleImageRemove}
                        className="p-0.5 text-gray-400 hover:text-red-500 transition-colors ml-1"
                        title="Remove image"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="inline-block">
                      <AttachmentDropdown
                        onFileSelect={handleImageSelect}
                        acceptedTypes={["image/*"]}
                        multiple={false}
                        className=""
                      />
                    </div>
                  )}
                </span>
              </div>
            )}
          </div>


          {/* Action buttons */}
          {status === "pending" && (!toolExecution || toolExecution.status === "started") && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleApprove}
                disabled={isUploading}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                  "bg-blue-500 text-white hover:bg-blue-600",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isUploading ? "Uploading..." : "Approve"}
              </button>
              <button
                onClick={handleDeny}
                disabled={isUploading}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                  "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                Deny
              </button>
            </div>
          )}
          
          {/* Show failure reason if available */}
          {toolExecution?.status === 'failed' && toolExecution.reason && (
            <div className="mt-2 text-sm text-gray-600">
              <span className="font-medium">Reason:</span> {toolExecution.reason}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
